import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { getApiUrl, API_CONFIG } from '../config/apiConfig.js';

const EmailVerification = ({ token, onVerificationComplete }) => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.AUTH.VERIFY_EMAIL), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage('Email verified successfully! You can now log in.');
        toast.success('Email verified successfully!');
        
        // Call callback after a delay to allow user to see the success message
        setTimeout(() => {
          onVerificationComplete(true);
        }, 2000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Email verification failed');
        toast.error(data.message || 'Email verification failed');
        
        setTimeout(() => {
          onVerificationComplete(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setStatus('error');
      setMessage('Network error. Please try again.');
      toast.error('Network error. Please try again.');
      
      setTimeout(() => {
        onVerificationComplete(false);
      }, 3000);
    }
  };

  const resendVerification = async () => {
    try {
      const email = localStorage.getItem('pendingVerificationEmail');
      if (!email) {
        toast.error('No email found for resending verification');
        return;
      }

      const response = await fetch(getApiUrl(API_CONFIG.AUTH.RESEND_VERIFICATION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Verification email sent successfully!');
      } else {
        toast.error(data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error('Network error. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 shadow-lg ${
            status === 'success' 
              ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 shadow-emerald-500/25'
              : status === 'error'
              ? 'bg-gradient-to-r from-red-500 to-pink-500 shadow-red-500/25'
              : 'bg-gradient-to-r from-blue-500 to-purple-500 shadow-blue-500/25'
          }`}>
            {status === 'verifying' && (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            )}
            {status === 'success' && (
              <CheckCircleIcon className="h-8 w-8 text-white" />
            )}
            {status === 'error' && (
              <XCircleIcon className="h-8 w-8 text-white" />
            )}
          </div>
          <h1 className={`text-3xl font-bold bg-clip-text text-transparent mb-2 ${
            status === 'success'
              ? 'bg-gradient-to-r from-emerald-400 to-cyan-400'
              : status === 'error'
              ? 'bg-gradient-to-r from-red-400 to-pink-400'
              : 'bg-gradient-to-r from-blue-400 to-purple-400'
          }`}>
            {status === 'verifying' && 'Verifying Email'}
            {status === 'success' && 'Email Verified!'}
            {status === 'error' && 'Verification Failed'}
          </h1>
          <p className="text-gray-300 text-sm">{message}</p>
        </div>

        {/* Content */}
        <div className="bg-gray-900/80 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-700/50 p-8 text-center">
          {status === 'success' && (
            <div className="space-y-6">
              <div className="p-4 bg-emerald-500/20 border border-emerald-500/50 rounded-xl">
                <p className="text-emerald-300 text-sm">{message}</p>
              </div>
              <button
                onClick={() => onVerificationComplete(true)}
                className="w-full py-3 px-6 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
              >
                <span>Continue to Login</span>
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
                <p className="text-red-300 text-sm">{message}</p>
              </div>
              <div className="space-y-3">
                <button
                  onClick={resendVerification}
                  className="w-full py-3 px-6 rounded-xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Resend Verification Email
                </button>
                <button
                  onClick={() => onVerificationComplete(false)}
                  className="w-full py-3 px-6 rounded-xl font-bold bg-gray-600 hover:bg-gray-700 text-white transition-all duration-200"
                >
                  Go Back
                </button>
              </div>
            </div>
          )}

          {status === 'verifying' && (
            <div className="space-y-6">
              <div className="p-4 bg-blue-500/20 border border-blue-500/50 rounded-xl">
                <p className="text-blue-300 text-sm">{message}</p>
              </div>
              <div className="flex items-center justify-center space-x-2 text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                <span className="text-sm">Please wait...</span>
              </div>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-gray-800/50 backdrop-blur-lg rounded-xl border border-gray-700/50">
          <h3 className="text-lg font-semibold text-white mb-3 text-center">Need Help?</h3>
          <div className="space-y-2 text-sm text-gray-300">
            <p>• Check your spam folder for the verification email</p>
            <p>• Make sure you clicked the link within 24 hours</p>
            <p>• Contact support if you continue having issues</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
